import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Alert,
  Box
} from '@mui/material';
import { axiosPrivate } from "../../../api/axios";

const MachineStatusDialog = ({ 
  open, 
  onClose, 
  onSave, 
  data, 
  mode,
  projectId,
  group_code,
  project_code,
  existingData = [] // 기존 데이터 추가
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [alertMessage, setAlertMessage] = useState('');
  const [statusList, setStatusList] = useState([]); // 상태 목록

  // 상태 목록 조회 - 디버깅 로그 추가
  const fetchStatusList = async () => {
    console.log('상태 목록 조회 시작:', { group_code, project_code, projectId });
    
    try {
      const response = await axiosPrivate.post('/table/statusmaster/list', {
        project_id: projectId,
        group_code,
        project_code
      });
      
      console.log('상태 목록 응답:', response.data);
      setStatusList(response.data.data || []);
    } catch (error) {
      console.error('상태 목록 조회 실패:', error);
      console.error('에러 상세:', error.response?.data);
      setStatusList([]);
      
      // 기본 상태 목록 설정 (fallback)
      setStatusList([
        { statusId: 1, statusName: '기본 상태' },
        { statusId: 2, statusName: '운전' },
        { statusId: 3, statusName: '정지' }
      ]);
    }
  };

  // 다이얼로그가 열릴 때 데이터 초기화
  useEffect(() => {
    if (open) {
      console.log('다이얼로그 열림, 상태 목록 조회 시작');
      // 상태 목록 조회
      fetchStatusList();
      
      if (mode === 'edit' && data) {
        // 편집 모드일 때 기존 데이터로 초기화
        console.log('편집 모드 데이터:', data);
        setFormData({...data});
      } else {
        // 추가 모드일 때 빈 데이터로 초기화
        setFormData({
          mchId: '',
          mesMchId: '',
          statusId: '1',
          lampId: '0',
          boardType: '0',
          monitorTitle: '',
          monitorText: '',
          monTextSize: '15',
          monTextHide: '0',
          monitorData: '',
          monitorProc: '',
          procStartTime: '',
          bufferIn: '0',
          bufferOut: '0'
        });
      }
      
      setErrors({});
      setAlertMessage('');
    }
  }, [open, data, mode, group_code, project_code]);

  // 입력 필드 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 에러 상태 초기화
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // 장비 ID 중복 검사 (추가 모드에서만)
    if (name === 'mchId' && mode === 'add' && value) {
      const isDuplicate = existingData.some(item => item.mchId === value);
      if (isDuplicate) {
        setErrors(prev => ({
          ...prev,
          mchId: '이미 존재하는 장비 ID입니다.'
        }));
      }
    }
  };

  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.mchId) {
      newErrors.mchId = '장비 ID를 입력하세요';
    } else if (mode === 'add') {
      // 추가 모드에서 중복 검사
      const isDuplicate = existingData.some(item => item.mchId === formData.mchId);
      if (isDuplicate) {
        newErrors.mchId = '이미 존재하는 장비 ID입니다.';
      }
    }
    
    if (!formData.mesMchId) {
      newErrors.mesMchId = 'MES ID를 입력하세요';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 저장 핸들러
  const handleSave = () => {
    if (validateForm()) {
      const submitData = {...formData};
      
      // 숫자 필드들을 숫자로 변환
      const numericFields = ['statusId', 'lampId', 'boardType', 'monTextSize', 'monTextHide', 'bufferIn', 'bufferOut'];
      numericFields.forEach(field => {
        if (submitData[field] !== null && submitData[field] !== undefined && submitData[field] !== '') {
          submitData[field] = Number(submitData[field]);
        } else {
          // 기본값 설정
          if (field === 'statusId') {
            submitData[field] = 1;
          } else if (field === 'monTextSize') {
            submitData[field] = 15;
          } else {
            submitData[field] = 0;
          }
        }
      });
      
      // 추가 모드에서는 id가 필요 없으므로 제거
      if (mode === 'add') {
        delete submitData.id;
        delete submitData.updateDate;
      }
      
      console.log('저장할 데이터:', submitData);
      onSave(submitData, mode);
    } else {
      setAlertMessage('필수 항목을 모두 입력하세요.');
    }
  };

  console.log('현재 상태 목록:', statusList);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
        {mode === 'add' ? 'Machine Status 추가' : 'Machine Status 편집'}
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        {alertMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {alertMessage}
          </Alert>
        )}
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="mchId"
              label="장비 ID"
              fullWidth
              value={formData.mchId ?? ''}
              onChange={handleChange}
              error={!!errors.mchId}
              helperText={errors.mchId}
              disabled={mode === 'edit'} // 편집 모드에서는 ID 변경 불가
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="mesMchId"
              label="MES ID"
              fullWidth
              value={formData.mesMchId ?? ''}
              onChange={handleChange}
              error={!!errors.mesMchId}
              helperText={errors.mesMchId}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="status-id-label">상태 ID</InputLabel>
              <Select
                labelId="status-id-label"
                name="statusId"
                value={formData.statusId ?? '1'}
                onChange={handleChange}
                label="상태 ID"
              >
                {statusList.length > 0 ? (
                  statusList.map((status) => (
                    <MenuItem key={status.statusId} value={String(status.statusId)}>
                      {status.statusId} : {status.statusName || '이름 없음'}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="1">1 : 기본 상태</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="lampId"
              label="램프 ID"
              type="number"
              fullWidth
              value={formData.lampId ?? '0'}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="boardType"
              label="말풍선 타입"
              type="number"
              fullWidth
              value={formData.boardType ?? '0'}
              onChange={handleChange}
              helperText="숫자로 입력하세요"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="monTextSize"
              label="말풍선 텍스트 크기"
              type="number"
              fullWidth
              value={formData.monTextSize ?? '15'}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="monitorTitle"
              label="말풍선 제목"
              fullWidth
              value={formData.monitorTitle ?? ''}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="monitorText"
              label="말풍선 내용"
              fullWidth
              multiline
              rows={3}
              value={formData.monitorText ?? ''}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="text-hide-label">말풍선 숨김 여부</InputLabel>
              <Select
                labelId="text-hide-label"
                name="monTextHide"
                value={formData.monTextHide ?? '0'}
                onChange={handleChange}
                label="말풍선 숨김 여부"
              >
                <MenuItem value="0">0 : 말풍선 전부 표시</MenuItem>
                <MenuItem value="1">1 : 말풍선 비표시</MenuItem>
                <MenuItem value="2">2 : 말풍선 클릭 표시</MenuItem>
                <MenuItem value="3">3 : Title 표시 / Text 클릭 표시</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="monitorData"
              label="식별값"
              fullWidth
              value={formData.monitorData ?? ''}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="monitorProc"
              label="동작 코드"
              fullWidth
              value={formData.monitorProc ?? ''}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="procStartTime"
              label="동작 시작 시간"
              fullWidth
              value={formData.procStartTime ?? ''}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              name="bufferIn"
              label="버퍼(인)"
              type="number"
              fullWidth
              value={formData.bufferIn ?? '0'}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="bufferOut"
              label="버퍼(아웃)"
              type="number"
              fullWidth
              value={formData.bufferOut ?? '0'}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary">
          저장
        </Button>
        <Button onClick={onClose} color="inherit">
          취소
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MachineStatusDialog; 